import {
  Box,
  Button,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Fragment, useCallback, useMemo, useState } from "react";
import { MUTATIONS } from "../../../graphql";
import {
  Apartment,
  ApartmentRole,
  ApartmentUserPermissions,
} from "../../../graphql/models";
import { useSnackbarMessage, useSnackbarMutation } from "../../../hooks";
import { LoadWrapper } from "../load_wrapper";
import { PhoneInput } from "../phone_input";
import PermissionsPicker from "./permissions_picker/permissions_picker";
import ApartmentRolePicker from "./role_picker/role_picker";

enum StepperMode {
  KnownUser,
  NewUser,
}

export interface AddUserToApartmentWizardProps {
  apartment: Apartment;
  onAdd: () => void;
}

export function AddUserToApartmentWizard(props: AddUserToApartmentWizardProps) {
  const { apartment, onAdd } = props;

  const { raiseSuccessSnackbar } = useSnackbarMessage();

  const [invokeAddApartmentUser, { loading }] = useSnackbarMutation(
    MUTATIONS.ADD_APARTMENT_USER,
    {
      onCompleted: () => {
        raiseSuccessSnackbar("Ospite aggiunto con successo!");
        onAdd();
      },
    }
  );

  const [username, setUsername] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [confirmPassword, setConfirmPassword] = useState<string>();
  const [apartmentRole, setApartmentRole] = useState<ApartmentRole>(
    ApartmentRole.Guest
  );
  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const [stepperMode, setStepperMode] = useState<StepperMode>();
  const [permissions, setPermissions] = useState<ApartmentUserPermissions>({
    canReserveAmenities: true,
    domotics: {
      alarm: false,
      clima: false,
      lights: false,
      shutters: false,
    },
  });

  const [step, setStep] = useState(0);

  const apartmentCanAddAdmin = useMemo(() => {
    return (
      apartment.users?.some((v) => v.role === ApartmentRole.Admin) === false
    );
  }, [apartment]);
  const addApartmentAdminWarn = useMemo(() => {
    if (apartmentRole === ApartmentRole.Admin && apartment.users.length > 0) {
      return `Attenzione: aggiungendo un admin verranno applicati tutti i flussi di autorimozione degli utenti ed alterazione dei permessi owner.`;
    }
  }, [apartment, apartmentRole]);

  const triggerSetStepperMode = (mode: StepperMode) => {
    setStepperMode(mode);
    setStep(1);
  };

  const userDataCompleted = useMemo(() => {
    if (stepperMode === StepperMode.KnownUser) {
      return [Boolean(username), Boolean(apartmentRole)].every(
        (v) => v === true
      );
    } else {
      return (
        [
          Boolean(username),
          Boolean(password),
          Boolean(apartmentRole),
          Boolean(firstName),
          Boolean(lastName),
          Boolean(email),
          Boolean(phone),
          Boolean(permissions),
        ].every((v) => v === true) && password === confirmPassword
      );
    }
  }, [
    apartmentRole,
    confirmPassword,
    email,
    firstName,
    lastName,
    password,
    phone,
    stepperMode,
    username,
    permissions,
  ]);

  const currentUserData = useMemo(() => {
    if (stepperMode === StepperMode.KnownUser) {
      return {
        username,
        apartmentRole,
        permissions,
      };
    } else {
      return {
        username,
        apartmentRole,
        password,
        firstName,
        lastName,
        email,
        phone,
        permissions,
      };
    }
  }, [
    apartmentRole,
    email,
    firstName,
    lastName,
    password,
    permissions,
    phone,
    stepperMode,
    username,
  ]);

  const triggerAddGuest = useCallback(() => {
    invokeAddApartmentUser({
      variables: {
        userData: {
          ...currentUserData,
        },
        apartmentId: apartment.id,
      },
    });
  }, [apartment.id, currentUserData, invokeAddApartmentUser]);

  return (
    <LoadWrapper loading={loading}>
      <Box style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Stepper activeStep={step} style={{ width: "100%" }}>
          <Step key={"user_origin"} completed={stepperMode !== void 0}>
            <StepLabel>Specifica l'origine dell'utente.</StepLabel>
          </Step>
          <Step key={"user_data"} completed={userDataCompleted}>
            <StepLabel>Specifica l'origine dell'utente.</StepLabel>
          </Step>
        </Stepper>
        <Grid container spacing={3} xs={12} style={{ width: "100%" }}>
          {step === 0 && (
            // Ask if mode is search existing user or add one
            <Fragment>
              <Grid item xs={12}>
                <Typography variant="body1">
                  Specifica se la persona da aggiungere all'appartamento è un
                  utente esistente oppure se si tratta di un nuovo utente.
                  <br />
                  Se si tratta di in nuovo utente, verranno richiesti tutti i
                  dati anagrafici: assicurati di avere a portata di mano gli
                  estremi della persona, un numero di cellulare valido ed
                  un'email valida.
                  <br />
                  Se invece si tratta di un utente esistente, verrà richiesto
                  solo lo username con cui quest'ultimo accede.
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <Button
                  color="default"
                  variant="contained"
                  onClick={() => triggerSetStepperMode(StepperMode.KnownUser)}
                >
                  L'utente esiste già ed ho il suo nome utente.
                </Button>
                <Button
                  color="default"
                  variant="contained"
                  onClick={() => triggerSetStepperMode(StepperMode.NewUser)}
                >
                  Si tratta di in nuovo utente
                </Button>
              </Grid>
            </Fragment>
          )}
          {step === 1 && (
            <Fragment>
              {stepperMode === StepperMode.NewUser && (
                <Fragment>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nome"
                      variant="outlined"
                      style={{ width: "100%" }}
                      defaultValue={firstName}
                      onChange={(evt) => setFirstName(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Cognome"
                      variant="outlined"
                      style={{ width: "100%" }}
                      defaultValue={lastName}
                      onChange={(evt) => setLastName(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Indirizzo email"
                      variant="outlined"
                      type="email"
                      style={{ width: "100%" }}
                      defaultValue={email}
                      onChange={(evt) => setEmail(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PhoneInput
                      label="Numero di cellulare"
                      variant="outlined"
                      type="email"
                      style={{ width: "100%" }}
                      defaultValue={phone}
                      onChange={(evt) => setPhone(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ApartmentRolePicker
                      canAddAdmin={apartmentCanAddAdmin}
                      onRoleChange={setApartmentRole}
                      role={apartmentRole}
                    />
                    {addApartmentAdminWarn ?? ""}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PermissionsPicker
                      onPermissionsChange={setPermissions}
                      permissions={permissions}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}></Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Username"
                      variant="outlined"
                      style={{ width: "100%" }}
                      defaultValue={username}
                      onChange={(evt) => setUsername(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Password"
                      variant="outlined"
                      type="password"
                      style={{ width: "100%" }}
                      defaultValue={password}
                      onChange={(evt) => setPassword(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Conferma password"
                      variant="outlined"
                      type="password"
                      style={{ width: "100%" }}
                      defaultValue={confirmPassword}
                      onChange={(evt) => setConfirmPassword(evt.target.value)}
                    />
                  </Grid>
                </Fragment>
              )}
              {stepperMode === StepperMode.KnownUser && (
                <Fragment>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      label="Indica qui lo username dell'utente"
                      variant="outlined"
                      style={{ width: "100%" }}
                      defaultValue={username}
                      onChange={(evt) => setUsername(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <ApartmentRolePicker
                      canAddAdmin={apartmentCanAddAdmin}
                      onRoleChange={setApartmentRole}
                      role={apartmentRole}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <PermissionsPicker
                      onPermissionsChange={setPermissions}
                      permissions={permissions}
                    />
                  </Grid>
                </Fragment>
              )}
              <Grid item xs={12}>
                <Button
                  color="default"
                  variant="contained"
                  disabled={!userDataCompleted}
                  onClick={() => triggerAddGuest()}
                >
                  AGGIUNGI OSPITE
                </Button>
              </Grid>
            </Fragment>
          )}
        </Grid>
      </Box>
    </LoadWrapper>
  );
}
